import {enableProdMode, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection} from '@angular/core';
import {environment} from './environments/environment';
import {StateConfigService} from './app/services/state/state-config.service';
import {provideRouter, RouterModule, withRouterConfig} from '@angular/router';
import {JWT_OPTIONS, JwtHelperService} from '@auth0/angular-jwt';
import {LoadingService} from './app/services/loading/loading.service';
import {AuthGuardService as AuthGuard} from './app/services/auth/auth-guard.service';
import {DatePipe, registerLocaleData} from '@angular/common';
import {FilterByListPipe} from './app/pipes/filterByList.pipe';
import {LphToNumberPipe, NumberToLphPipe} from './app/pipes/lphToNumber.pipe';
import {LphPipe} from './app/pipes/lph.pipe';
import {TaskStatePipe, TaskStateReversePipe} from './app/pipes/task-state.pipe';
import {TaskDetailsService} from './app/services/tasks/task-details.service';
import {ProjectKindPipe} from './app/pipes/project-kind.pipe';
import {CheckTypePipe} from './app/pipes/check-type.pipe';
import {LodPipe} from './app/pipes/lod.pipe';
import {FeatureFlagGuardService as FeatureFlagGuard} from './app/services/featureFlags/feature-flag-guard.service';
import {RoleAndPrivilegesPipe} from './app/pipes/role-and-privileges.pipe';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr} from '@angular/common/http';
import {HttpLoadingInterceptor} from './app/interceptors/http-loading-interceptor.service';
import {HttpErrorInterceptor} from './app/interceptors/http-error.interceptor';
import {RequestDedupInterceptor} from './app/interceptors/request-dedup.interceptor';
import {AuthInterceptor} from './app/interceptors/auth.interceptor';
import {MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig} from '@angular/material/dialog';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {bootstrapApplication} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideMarkdown} from 'ngx-markdown';

import {AppComponent} from './app/app.component';
import {HoldableDirective} from "./app/directives/holdable.directive";
import {routes} from './app/routes.config';
import {MomentDateAdapter} from "@angular/material-moment-adapter";
import {ApiModule, BASE_PATH} from "./app/api";
import {API_BASE_PATH} from './environments/api-base-path';
import {ApiModule as BimApiModule} from "./app/bim-api";
import {ApiModule as QualityApiModule} from "./app/quality-api";
import {ApiModule as ChatBICApiModule} from "./app/chatBIC-api";
import localeDE from '@angular/common/locales/de';

import {PendingWorkUnloadGuardService} from './app/services/unload/pending-work-unload-guard.service';

const DateFormats = {
  parse: {
    dateInput: ['DD.MM.YYYY']
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

registerLocaleData(localeDE);

export function tokenGetter() {
  return localStorage.getItem('token');
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    StateConfigService,
    RouterModule,
    JwtHelperService,
    LoadingService,
    AuthGuard,
    DatePipe,
    FilterByListPipe,
    LphToNumberPipe,
    NumberToLphPipe,
    LphPipe,
    TaskStatePipe,
    TaskStateReversePipe,
    TaskDetailsService,
    ProjectKindPipe,
    CheckTypePipe,
    LodPipe,
    FeatureFlagGuard,
    RoleAndPrivilegesPipe,
    HoldableDirective,
    JwtHelperService,

    importProvidersFrom(ApiModule),
    importProvidersFrom(BimApiModule),
    importProvidersFrom(QualityApiModule),
    importProvidersFrom(ChatBICApiModule),
    // Nur in der Serve-Konfiguration `beta` gesetzt (Dev-Proxy), sonst gilt der generierte basePath.
    ...(API_BASE_PATH ? [{provide: BASE_PATH, useValue: API_BASE_PATH}] : []),

    // JwtHelperService braucht JWT_OPTIONS (tokenGetter fuer Aufrufe ohne Token-Argument). Den
    // Interceptor von angular-jwt gibt es nicht mehr – das Token haengt der AuthInterceptor an.
    {provide: JWT_OPTIONS, useValue: {tokenGetter: tokenGetter}},

    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimations(),
    // Angular 22 erbt Routenparameter standardmaessig von allen Elternrouten ('always'). routes.config.ts
    // verschachtelt mehrere ':id'-Routen (Projekt > Datei bzw. Aufgabe), deren Werte sich sonst
    // ueberschreiben koennten. 'emptyOnly' ist das Verhalten bis Angular 21.
    provideRouter(routes, withRouterConfig({paramsInheritanceStrategy: 'emptyOnly'})),
    provideMarkdown(),

    // Als erster: holt vor jedem Request ein gueltiges Token und wiederholt nach einem 401 einmal mit
    // erneuertem Token. Vor dem Dedup, damit jeder Aufrufer selbst wiederholt. Siehe AuthInterceptor.
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    // Muss vor den uebrigen stehen: zusammengefasste Requests durchlaufen die inneren Interceptor
    // dann nicht mehr (Ladeindikator zählt einmal, Fehler meldet einmal). Siehe RequestDedupInterceptor.
    {provide: HTTP_INTERCEPTORS, useClass: RequestDedupInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: HttpLoadingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true},
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...new MatDialogConfig(),
        hasBackdrop: true,
        disableClose: true,
      } as MatDialogConfig,
    },
    {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
    {provide: MAT_DATE_FORMATS, useValue: DateFormats},
    {provide: MAT_DATE_LOCALE, useValue: 'de-DE'},
    {provide: LOCALE_ID, useValue: 'de-DE'},

    PendingWorkUnloadGuardService
  ]
})
  .then(appRef => {
    // global aktivieren (idempotent) – schützt auch, wenn man nicht in der Files-View ist
    const guard = appRef.injector.get(PendingWorkUnloadGuardService);
    guard.register();
  })
  .catch(err => console.error(err));
